import type { Database, DatabaseEntry, DatabaseField, DatabaseLinkGalleryPage } from '~/api-client';

/**
 * Die Kachelseite („Link-Gallery") — vierte Zusatzseite einer Datenbank.
 *
 * WAS SIE IST: Unter „Anzeige ▸ Seiten" schaltet der Betreiber eine Seite zu,
 * die aus den Datensaetzen einer Datenbank Kacheln baut — Kategorienleiste,
 * Suchschlitz, je Kachel Abbildung, Beschreibung und Link. Die drei anderen
 * Seiten sind freie Texte; diese hier wird nicht geschrieben, sondern
 * ZUGEORDNET: vier Rollen zeigen auf vier Felder der Quelle.
 *
 * WARUM DIESE DATEI: Dieselbe Zuordnung wird an zwei Enden gebraucht — die
 * Konfigurationsmaske belegt sie vor und laesst waehlen, die Leseransicht
 * loest sie auf. Zweimal geschrieben liefen sie auseinander, sobald eine
 * Rolle dazukommt. Hier steht sie einmal und ist per Vitest pruefbar.
 *
 * WOHER DIE QUELLE KOMMT: `sourceAlias` ist der Teil hinter
 * datenbankgestalter.de/ — leer heisst „die eigene Datenbank". Eine fremde
 * Quelle liefert nur, wenn sie anonym ohne Kennwort offen ist und ein
 * Leserecht traegt; das entscheidet ihre Rechte-Maske, nicht diese Datei
 * (gemessen 28.09.2026: der Zugang einer Datenbank liest die Eintraege einer
 * anderen, `assertDbAccess` fragt nicht nach der Identitaet).
 */

/** Die vier Rollen, in der Reihenfolge der Maske. */
export type GalerieRolle = 'kategorie' | 'abbildung' | 'link' | 'beschreibung';

/** Rolle → Feld in der gespeicherten Einstellung. */
export const ROLLE_FELD: Record<GalerieRolle, keyof DatabaseLinkGalleryPage> = {
  kategorie: 'categoryFieldId',
  abbildung: 'imageFieldId',
  link: 'linkFieldId',
  beschreibung: 'captionFieldId',
};

export interface RollenBeschreibung {
  /** Beschriftung in der Maske. */
  titel: string;
  /**
   * Feldtitel, bei denen vorbelegt wird. Der erste ist der Regelfall, die
   * weiteren sind gaengige Schreibweisen — verglichen wird ohne Ruecksicht
   * auf Gross-/Kleinschreibung.
   */
  titelgleich: string[];
  /** Feldarten, die fuer diese Rolle zur Auswahl stehen. Leer = alle. */
  feldarten: string[];
  /** Ohne sie gibt es keine Kacheln. */
  pflicht: boolean;
}

export const GALERIE_ROLLEN: Record<GalerieRolle, RollenBeschreibung> = {
  kategorie: {
    titel: 'Kategorie',
    titelgleich: ['Kategorie', 'Bereich', 'Gruppe'],
    feldarten: ['CATEGORY_FIELD', 'TEXT_FIELD'],
    pflicht: false,
  },
  abbildung: {
    titel: 'Abbildung',
    titelgleich: ['Abbildung', 'Bild', 'Foto'],
    feldarten: ['IMAGE_FIELD'],
    pflicht: false,
  },
  link: {
    titel: 'Link',
    titelgleich: ['Link', 'URL', 'Adresse', 'Webseite'],
    feldarten: ['LINK_FIELD', 'TEXT_FIELD'],
    pflicht: true,
  },
  beschreibung: {
    titel: 'Beschreibung',
    titelgleich: ['Beschreibung', 'Bezeichnung', 'Name', 'Titel'],
    feldarten: ['TEXT_FIELD', 'COMBINED_TEXT_FIELD'],
    pflicht: true,
  },
};

/** Suchmaschine → Adresse, an die der Suchschlitz uebergibt. */
export const SUCHMASCHINE_URL: Record<string, string> = {
  GOOGLE: 'https://www.google.com/search',
  BING: 'https://www.bing.com/search',
};

/** Vergleichsform eines Feldtitels — wie beim Link-Befehl grosszuegig. */
const vergleichbar = (name: string | null | undefined): string =>
  (name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/** Die Felder, die fuer eine Rolle ueberhaupt in Frage kommen. */
export function kandidaten(felder: DatabaseField[] | undefined, rolle: GalerieRolle): DatabaseField[] {
  const erlaubt = GALERIE_ROLLEN[rolle].feldarten;
  return (felder ?? []).filter(f =>
    !!f?.id && (erlaubt.length === 0 || erlaubt.includes(String((f as any).fieldType))));
}

/**
 * Vorbelegung bei Titelgleichheit.
 *
 * Nur Felder, die auch zur Rolle passen, werden vorbelegt — ein Textfeld
 * namens „Abbildung" ist keine Abbildung. Gesucht wird in der Reihenfolge der
 * Schreibweisen, damit „Beschreibung" vor „Name" gewinnt, wenn es beide gibt.
 */
export function vorbelegung(felder: DatabaseField[] | undefined): Partial<DatabaseLinkGalleryPage> {
  const treffer: Partial<DatabaseLinkGalleryPage> = {};
  for (const rolle of Object.keys(GALERIE_ROLLEN) as GalerieRolle[]) {
    const moeglich = kandidaten(felder, rolle);
    for (const schreibweise of GALERIE_ROLLEN[rolle].titelgleich) {
      const feld = moeglich.find(f => vergleichbar(f.name) === vergleichbar(schreibweise));
      if (feld?.id) {
        (treffer as any)[ROLLE_FELD[rolle]] = feld.id;
        break;
      }
    }
  }
  return treffer;
}

/**
 * Text aus einem Feldwert.
 *
 * Werte kommen je nach Feldart als Text, Zahl, Liste oder Objekt.
 * Kategoriefelder liefern den Options-WERT; die Beschriftung, die der Besucher
 * lesen soll, steht im Feld unter `content[].label` und wird hier
 * nachgeschlagen.
 */
export function alsText(wert: unknown, feld?: DatabaseField | null): string {
  if (wert === null || wert === undefined) return '';
  if (Array.isArray(wert)) {
    return wert.map(w => alsText(w, feld)).filter(Boolean).join(', ');
  }
  if (typeof wert === 'object') {
    const o = wert as Record<string, unknown>;
    return alsText(o.label ?? o.value ?? o.name ?? o.text ?? '', feld);
  }
  const text = String(wert).trim();
  const optionen = (feld as any)?.content;
  if (Array.isArray(optionen)) {
    const option = optionen.find((o: any) => o && o.value === text);
    if (option) return String(option.label ?? option.value ?? text).trim();
  }
  return text;
}

/**
 * Bei Mehrfachauswahl traegt die Kachel nur EINE Kategorie.
 *
 * Sonst entstuende aus „Sport, TV" eine dritte Kategorie, die es gar nicht
 * gibt, und die Leiste zeigte Eintraege, hinter denen nichts steht.
 */
export const ersterText = (wert: unknown, feld?: DatabaseField | null): string =>
  alsText(Array.isArray(wert) ? wert[0] : wert, feld);

/**
 * Verweis als Adresse.
 *
 * GEMESSEN am 27.09.2026 in einer echten Datenbank: Verweisfelder werden ohne
 * Vorsatz gepflegt („www.zdf.de"). Ohne Schema haelt der Browser das fuer
 * einen Pfad INNERHALB der Leseransicht und landet auf einer leeren Seite.
 */
export function alsAdresse(text: string): string {
  const roh = (text ?? '').trim();
  if (!roh) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(roh) || roh.startsWith('//')) return roh;
  return 'https://' + roh;
}

export interface Kachel {
  /** Kennung des Quell-Datensatzes — Schluessel beim Zeichnen. */
  id: string;
  kategorie: string;
  beschreibung: string;
  /** Fertige Zieladresse, Vorsatz ergaenzt. */
  url: string;
  /** Adresse der Abbildung, oder leer. */
  bildUrl: string;
}

/**
 * Datensaetze der Quelle → Kacheln.
 *
 * Ohne Beschreibung oder ohne Link ist eine Kachel nicht benutzbar; sie faellt
 * weg, statt als leeres Feld dazustehen. Die Abbildung ist freiwillig.
 *
 * `medienBasis` ist die Adresse des Backends; die Bilder kommen ueber den
 * OEFFENTLICHEN Weg `/db-media/…`, der keine Anmeldung verlangt — deshalb
 * zeigen sie auch, wenn die Quelle eine fremde Datenbank ist.
 */
export function kacheln(
  eintraege: DatabaseEntry[] | undefined,
  felder: DatabaseField[] | undefined,
  einstellung: DatabaseLinkGalleryPage | undefined,
  quelleIdOderAlias: string,
  medienBasis: string,
): Kachel[] {
  if (!einstellung) return [];
  const feldVon = (id?: string) => (felder ?? []).find(f => f.id && f.id === id) ?? null;

  const feldKategorie = feldVon(einstellung.categoryFieldId);
  const feldAbbildung = feldVon(einstellung.imageFieldId);
  const feldLink = feldVon(einstellung.linkFieldId);
  const feldText = feldVon(einstellung.captionFieldId);

  /*
   * ══ DAS ABGELEITETE FAVICON HAT KEINEN GESPEICHERTEN WERT ═════════════
   *
   * Betreiber 20.10.2026: „Rufe ich es über den Link auf, fehlen mir die
   * Logos. Rufe ich es über die Datenbank auf, sind sie da."
   *
   * GEMESSEN an seiner Datenbank „Lesezeichen": 122 Datensätze, davon
   * NULL mit gespeicherter Abbildung. Ihr Bildfeld ist vom Subtyp
   * `IMAGE_FIELD_DERIVED_FAVICON_TYPE` — das Logo wird aus der Webadresse
   * abgeleitet und über `/db-media/favicon/resolve` geholt, nicht aus dem
   * Datensatz gelesen. In der Datenbank macht das `DbImageField.vue`;
   * diese Kachelseite kannte den Fall nicht und baute die Adresse stur aus
   * einem Dateinamen, der nie dasteht. Darum blieben die Kacheln leer —
   * angemeldet genauso wie anonym.
   *
   * Die Quelle der Webadresse ist dieselbe wie beim Feld: `urlSourceFieldId`.
   * Fehlt sie, nimmt die Kachel ihren eigenen Link — sie hat ihn ohnehin.
   */
  const istFavicon = (feldAbbildung as any)?.type === 'IMAGE_FIELD_DERIVED_FAVICON_TYPE';
  const quellfeldDerAdresse = istFavicon
    ? feldVon((feldAbbildung as any)?.urlSourceFieldId) ?? feldLink
    : null;

  return (eintraege ?? []).map(eintrag => {
    const werte = (eintrag.fieldsValues ?? {}) as Record<string, unknown>;
    const bild = feldAbbildung?.id ? alsText(werte[feldAbbildung.id], feldAbbildung) : '';
    const adresseFuerFavicon = quellfeldDerAdresse?.id
      ? alsText(werte[quellfeldDerAdresse.id], quellfeldDerAdresse)
      : '';
    return {
      id: String(eintrag.id ?? ''),
      kategorie: feldKategorie?.id ? ersterText(werte[feldKategorie.id], feldKategorie) : '',
      beschreibung: feldText?.id ? alsText(werte[feldText.id], feldText) : '',
      url: feldLink?.id ? alsAdresse(alsText(werte[feldLink.id], feldLink)) : '',
      bildUrl: istFavicon
        ? (adresseFuerFavicon
          ? `${medienBasis}/db-media/favicon/resolve?url=${encodeURIComponent(adresseFuerFavicon)}`
          : '')
        : (bild && eintrag.id && feldAbbildung?.id
          ? `${medienBasis}/db-media/${quelleIdOderAlias}/${eintrag.id}/${feldAbbildung.id}/${encodeURIComponent(bild)}`
          : ''),
    };
  }).filter(k => !!k.url && !!k.beschreibung)
    .sort((a, b) => a.beschreibung.localeCompare(b.beschreibung, 'de'));
}

/**
 * Sammelplatz fuer Kacheln, die keine Kategorie tragen.
 *
 * OHNE IHN WAEREN SIE UNERREICHBAR — gemessen am 28.09.2026 an den
 * eingelesenen Edge-Lesezeichen: vier davon liegen direkt in der Leiste, ohne
 * Ordner. Die Leiste zeigte nur echte Kategorien, und gefiltert wurde auf die
 * gewaehlte — also erschienen diese vier auf keiner einzigen Ansicht. Kein
 * Mensch haette bemerkt, dass sie fehlen.
 */
export const OHNE_KATEGORIE = 'Ohne Kategorie';

/**
 * Die Kategorien, alphabetisch (Vorgabe des Betreibers 28.09.2026).
 *
 * `localeCompare` mit 'de', nicht der Vergleich von Zeichencodes: Sonst
 * stuende „Ärzte" hinter „Zoo" und jedes Wort mit grossem Anfangsbuchstaben
 * vor jedem kleinen.
 *
 * Der Sammelplatz steht ANS ENDE, nicht an seinen alphabetischen Platz — er
 * ist keine Kategorie, sondern der Rest.
 */
export const kategorien = (alle: Kachel[]): string[] => {
  const echte = [...new Set(alle.map(k => k.kategorie).filter(Boolean))]
    .sort((a, b) => a.localeCompare(b, 'de'));
  return alle.some(k => !k.kategorie) ? [...echte, OHNE_KATEGORIE] : echte;
};

/**
 * Die Farben der Kategorien — Beschriftung → Grund und Schrift.
 *
 * ══ WARUM NICHT IN `kategorien()` ════════════════════════════════════════
 *
 * Die Leiste arbeitet mit Beschriftungen (`Kachel.kategorie`), nicht mit
 * gespeicherten Werten. Das soll so bleiben: Filtern, Sortieren und der
 * Sammelplatz „Ohne Kategorie" haengen daran. Die Farben kommen deshalb
 * als eigene Nachschlagetabelle dazu, ebenfalls ueber die Beschriftung.
 *
 * ══ WOFUER ═══════════════════════════════════════════════════════════════
 *
 * Betreiber 20.10.2026: „Bei slides=gallery wird auch die Farbe der Pille
 * aus der Kategorie nicht uebernommen. Hintergrundfarbe und Schriftfarbe."
 *
 * In seiner Datenbank „Lesezeichen" tragen zwei der zwanzig Auspraegungen
 * eigene Farben („Apps" rot, „Nachrichten" gruen, beide mit weisser
 * Schrift). In der Listenansicht zeigt die Kachel sie laengst; die
 * Kategorienleiste der Kachelseite malte dagegen jede Pille gleich.
 *
 * BEIDE FARBEN ODER KEINE: Die Schrift einer Auspraegung ist zu ihrem Grund
 * gewaehlt. Einzeln weitergereicht stuende sie auf einer fremden Flaeche —
 * genau der Fehler, der am 19.10.2026 in der Auswahlliste weiss auf weiss
 * ergab. Steht nur eine der beiden da, bleibt die Pille beim Schema.
 */
export function kategorieFarben(
  felder: DatabaseField[] | undefined,
  einstellung: DatabaseLinkGalleryPage | undefined,
): Record<string, { grund: string; schrift: string }> {
  const feld = (felder ?? []).find(f => f.id && f.id === einstellung?.categoryFieldId) as any;
  const aus: Record<string, { grund: string; schrift: string }> = {};
  for (const o of (feld?.content ?? []) as any[]) {
    const grund = typeof o?.color === 'string' ? o.color.trim() : '';
    const schrift = typeof o?.textColor === 'string' ? o.textColor.trim() : '';
    if (!grund || !schrift) continue;
    const beschriftung = String(o?.label ?? o?.value ?? '').trim();
    if (beschriftung) aus[beschriftung] = { grund, schrift };
  }
  return aus;
}

/** Die Kacheln einer Leisten-Auswahl — der Sammelplatz meint „ohne Kategorie". */
export const kachelnDerKategorie = (alle: Kachel[], gewaehlt: string | null): Kachel[] => {
  if (!gewaehlt) return alle;
  if (gewaehlt === OHNE_KATEGORIE) return alle.filter(k => !k.kategorie);
  return alle.filter(k => k.kategorie === gewaehlt);
};

/** Ist die Kachelseite dieser Datenbank benutzbar? */
export function galerieAktiv(database: Database | null | undefined): boolean {
  const g = (database as any)?.additionalPages?.linkGallery as DatabaseLinkGalleryPage | undefined;
  return !!g?.enabled && !!g?.linkFieldId && !!g?.captionFieldId;
}

/** Die Einstellung dieser Datenbank, oder null. */
export function galerie(database: Database | null | undefined): DatabaseLinkGalleryPage | null {
  const g = (database as any)?.additionalPages?.linkGallery as DatabaseLinkGalleryPage | undefined;
  return g?.enabled ? g : null;
}

/** Was dem Besucher gesagt wird, wenn die Quelle nichts hergibt. */
export const QUELLE_VERSCHLOSSEN =
  'Diese Datenbank gibt ihre Einträge nicht frei. Bitte wenden Sie sich an den Betreiber.';
